import React, { useRef, useEffect, useState } from 'react';
import { useEditorStore, useCurrentHeadline, useCurrentSubhead, useTemplateStore } from '@/stores';
import { CanvasControls } from './CanvasControls';
import { LayerRenderer } from './LayerRenderer';
import { useCanvasInteraction } from '@/hooks/useCanvasInteraction';
import { useCanvasDropzone } from '@/hooks/useCanvasDropzone';
import { getCanvasBackgroundCSS } from '@/types/template';
import { getAllTemplates } from '@/templates';
import { FloatingToolbar } from '@/components/toolbar';
import { ShapeDrawPreview } from './ShapeDrawPreview';
import { SmartGuides } from './SmartGuides';
import { ColorPickerOverlay } from './ColorPickerOverlay';
import { DropzoneOverlay } from './DropzoneOverlay';
import { SelectionOverlay } from './SelectionOverlay';

export function Canvas() {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    selectedFormat,
    content,
    zoom,
    panOffset,
    showGrid,
    fitToView,
    setZoom,
    panBy,
  } = useEditorStore();

  const {
    currentTemplate,
    selectedLayerIds,
    hoveredLayerId,
    variableValues,
    loadTemplate,
  } = useTemplateStore();

  const headline = useCurrentHeadline();
  const sub = useCurrentSubhead();

  // Canvas interaction handlers
  const {
    handleLayerClick,
    handleCanvasClick,
    handleLayerHover,
    handleMouseDown,
    handleCanvasMouseDown,
    handleContainerMouseDown,
    handleResizeStart,
    isDrawing,
    isResizing,
    shapeDrawState,
    toolCursor,
    effectiveTool,
    guides,
    colorPickerState,
  } = useCanvasInteraction({ format: selectedFormat, enabled: !!currentTemplate });

  // Drag and drop / paste support
  const { dropzoneState, dropzoneProps } = useCanvasDropzone({
    enabled: true,
    canvasWidth: selectedFormat.width,
    canvasHeight: selectedFormat.height,
  });

  // Track canvas offset for smart guides
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });

  // Update canvas offset when canvas position changes
  useEffect(() => {
    const updateOffset = () => {
      const canvas = document.getElementById('ad-canvas');
      const container = containerRef.current;
      if (canvas && container) {
        const containerRect = container.getBoundingClientRect();
        const canvasRect = canvas.getBoundingClientRect();
        setCanvasOffset({
          x: canvasRect.left - containerRect.left,
          y: canvasRect.top - containerRect.top,
        });
      }
    };

    updateOffset();
    // Update on resize
    window.addEventListener('resize', updateOffset);
    return () => window.removeEventListener('resize', updateOffset);
  }, [zoom, panOffset]);

  // Fit to view on mount, format change, and container resize
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateZoom = () => {
      const { clientWidth, clientHeight } = container;
      // Only fit to view if container has valid dimensions
      if (clientWidth > 0 && clientHeight > 0) {
        fitToView(clientWidth, clientHeight);
      }
    };

    // Initial fit
    updateZoom();

    // Watch for container size changes (e.g., when bottom panel opens/closes)
    const resizeObserver = new ResizeObserver(updateZoom);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [selectedFormat.id, fitToView]);

  // Auto-load default template if none selected
  useEffect(() => {
    if (!currentTemplate) {
      const templates = getAllTemplates();
      if (templates.length > 0) {
        loadTemplate(templates[0]);
      }
    }
  }, [currentTemplate, loadTemplate]);

  // Mouse wheel zoom and trackpad pan
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      // Zoom if Ctrl/Meta is held (like Figma) or pinch gesture
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setZoom(zoom + delta);
      } else {
        // Pan with trackpad scroll (no modifier keys)
        e.preventDefault();
        panBy(-e.deltaX, -e.deltaY);
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [zoom, setZoom, panBy]);

  const scaledWidth = selectedFormat.width * zoom;
  const scaledHeight = selectedFormat.height * zoom;

  // Get background style
  const canvasBackground = currentTemplate
    ? getCanvasBackgroundCSS(currentTemplate.canvas.background)
    : '#0a0e17';

  return (
    <div
      className="absolute inset-0 overflow-hidden bg-navy-dark/50"
      {...dropzoneProps}
    >
      {/* Grid background */}
      {showGrid && (
        <div className="absolute inset-0 canvas-grid pointer-events-none" />
      )}

      {/* Dropzone overlay */}
      {dropzoneState.isDraggingOver && (
        <DropzoneOverlay fileType={dropzoneState.draggedFileType} />
      )}

      {/* Canvas container - pb-80 accounts for bottom panel (~320px) */}
      <div
        ref={containerRef}
        className="absolute inset-0 flex items-center justify-center p-12 pb-80"
        onMouseDown={handleContainerMouseDown}
        style={{ cursor: effectiveTool === 'hand' ? toolCursor : undefined }}
      >
        <div
          className="relative"
          style={{
            width: scaledWidth,
            height: scaledHeight,
            transform: `translate(${panOffset.x}px, ${panOffset.y}px)`,
          }}
        >
          {/* Format label */}
          <div className="absolute -top-7 left-0 font-mono text-[11px] text-fg-subtle tracking-wider uppercase whitespace-nowrap">
            {selectedFormat.name} ·{' '}
            <span className="text-primary-light">
              {selectedFormat.width}×{selectedFormat.height}
            </span>{' '}
            · {selectedFormat.platforms}
            {currentTemplate && (
              <span className="ml-2 text-primary">
                · {currentTemplate.metadata.name}
              </span>
            )}
          </div>

          {/* Ad frame */}
          <div
            id="ad-canvas"
            className="bg-navy rounded-md overflow-hidden shadow-2xl"
            style={{
              width: selectedFormat.width,
              height: selectedFormat.height,
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
              boxShadow: '0 40px 120px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)',
              background: canvasBackground,
              position: 'relative',
              cursor: toolCursor,
            }}
            onClick={handleCanvasClick}
            onMouseDown={handleCanvasMouseDown}
          >
            {currentTemplate?.layers ? (
              <LayerRenderer
                layers={currentTemplate.layers}
                format={selectedFormat}
                variableValues={{
                  headline: headline,
                  subhead: sub || '',
                  cta: content.cta,
                  ...variableValues,
                }}
                selectedLayerIds={selectedLayerIds}
                hoveredLayerId={hoveredLayerId}
                onLayerClick={handleLayerClick}
                onLayerHover={handleLayerHover}
                onLayerMouseDown={handleMouseDown}
              />
            ) : (
              // Loading state
              <div className="absolute inset-0 flex items-center justify-center text-fg-muted text-sm">
                Loading template...
              </div>
            )}
          </div>

          {/* Selection info */}
          {currentTemplate && selectedLayerIds.length > 0 && (
            <div className="absolute -bottom-6 left-0 text-xs text-primary">
              {selectedLayerIds.length === 1
                ? `Selected: ${currentTemplate.layers.find(l => l.id === selectedLayerIds[0])?.name || 'Layer'}`
                : `${selectedLayerIds.length} layers selected`
              }
            </div>
          )}
        </div>
      </div>

      {/* Canvas controls */}
      <CanvasControls />

      {/* Format info bar */}
      <FormatInfoBar />

      {/* Floating toolbar */}
      <FloatingToolbar />

      {/* Shape draw preview */}
      {isDrawing && (
        <ShapeDrawPreview
          drawState={shapeDrawState}
          toolId={effectiveTool}
          zoom={zoom}
        />
      )}

      {/* Smart guides during drag */}
      {guides.length > 0 && (
        <SmartGuides
          guides={guides}
          zoom={zoom}
          panOffset={panOffset}
          canvasOffset={canvasOffset}
        />
      )}

      {/* Selection overlay with resize handles */}
      {selectedLayerIds.length > 0 && (
        <SelectionOverlay
          selectedLayerIds={selectedLayerIds}
          layers={currentTemplate?.layers ?? []}
          format={selectedFormat}
          zoom={zoom}
          panOffset={panOffset}
          canvasOffset={canvasOffset}
          onResizeStart={handleResizeStart}
          isResizing={isResizing}
        />
      )}

      {/* Color picker overlay */}
      <ColorPickerOverlay
        isActive={effectiveTool === 'colorPicker'}
        previewColor={colorPickerState.previewColor}
        pickedColor={colorPickerState.pickedColor}
        showPickedNotification={colorPickerState.showPickedNotification}
      />
    </div>
  );
}

function FormatInfoBar() {
  const { selectedFormat } = useEditorStore();
  const { currentTemplate } = useTemplateStore();
  const aspectRatio = (selectedFormat.width / selectedFormat.height).toFixed(3);

  return (
    <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
      <div className="inline-flex gap-6 bg-white/[0.03] rounded-xl px-4 py-2.5 text-xs pointer-events-auto">
        <div>
          <span className="text-fg-subtle uppercase tracking-wider text-[10px] font-semibold">
            Platform
          </span>
          <p className="text-fg font-medium mt-0.5">{selectedFormat.platforms}</p>
        </div>
        <div>
          <span className="text-fg-subtle uppercase tracking-wider text-[10px] font-semibold">
            Dimensions
          </span>
          <p className="text-fg font-medium mt-0.5">
            {selectedFormat.width} × {selectedFormat.height}
          </p>
        </div>
        <div>
          <span className="text-fg-subtle uppercase tracking-wider text-[10px] font-semibold">
            Aspect
          </span>
          <p className="text-fg font-medium mt-0.5">{aspectRatio}:1</p>
        </div>
        <div>
          <span className="text-fg-subtle uppercase tracking-wider text-[10px] font-semibold">
            Template
          </span>
          <p className="text-fg font-medium mt-0.5">
            {currentTemplate?.metadata.name || 'None'}
          </p>
        </div>
      </div>
    </div>
  );
}
